/**
 * Labstep
 *
 * @module components/FilterPost/Report/Numeric/Active/Post
 * @desc Active node component for numbers
 */

import type { FilterPostActiveLabelProps } from 'labstep-web/components/FilterPost/Active/Label/types';
import { FilterPostReportNumeric } from 'labstep-web/components/FilterPost/Report/Numeric';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import { PostFilter } from 'labstep-web/services/postFilter';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadableShort,
} from 'labstep-web/services/postFilter/constants';
import type React from 'react';
import {
  getPostFilterNumeric,
  getPostFilterNumericWithName,
} from '../../Form';
import { COMPARISON_OPTIONS } from '../../Form/fields';

export enum PostFilterComparisonHumanReadable {
  eq = 'equals',
  lte = 'less than or equal to',
  lt = 'less than',
  gte = 'greater than or equal to',
  gt = 'greater than',
}

export const FilterPostReportNumericActivePost: React.FC<
  FilterPostActiveLabelProps
> = ({ node, onRemoveClick }) => {
  const NUMERIC_FILTERS = [
    getPostFilterNumeric(
      'available_resource_item_count',
      PostFilterComparison.eq,
    ),
    getPostFilterNumeric(
      'amount',
      PostFilterComparison.eq,
      'protocolValues',
    ),
    getPostFilterNumericWithName('amount', PostFilterComparison.eq),
    getPostFilterNumeric('number', PostFilterComparison.eq),
  ];
  const { addPostFilter } = useParamsContext();
  const filterActive = NUMERIC_FILTERS.find((filter) =>
    filter.isNodeActive(node),
  );
  if (!filterActive) {
    return null;
  }

  const path = filterActive.getPath() || '';
  let attributes = filterActive.getAttributeValues(node);
  const protocolValueName = attributes.name?.value;
  if (filterActive.getPredicates().length > 1) {
    attributes = Object.fromEntries(
      Object.entries(filterActive.getAttributeValues(node)).filter(
        ([key, value]) => key !== 'name',
      ),
    );
  }

  const label = 'available_resource_item_count';
  const field = Object.keys(attributes)[0];
  const { comparison } = Object.values(attributes)[0];
  const value =
    comparison === PostFilterComparison.not_null
      ? ''
      : Object.values(attributes)[0].value;

  const defaultValues = {
    comparison: COMPARISON_OPTIONS.find(
      (option) => option.value === comparison,
    ),
    value,
  };

  const postFilterSettings = {
    label,
    path,
    field,
  };

  const comparaisonReadable =
    PostFilterComparisonHumanReadableShort[
      comparison as keyof typeof PostFilterComparisonHumanReadableShort
    ];
  return (
    <FilterPostReportNumeric
      label={label}
      path={postFilterSettings.path}
      field={postFilterSettings.field}
      addPostFilter={addPostFilter}
      defaultValues={defaultValues}
      viewComponent={({ toggleModal }) => (
        <RemoveLabel
          name={
            protocolValueName
              ? `${protocolValueName}: ${field} ${comparaisonReadable} ${value}`
              : `${field} ${comparaisonReadable} ${value}`
          }
          onRemoveClick={onRemoveClick}
        />
      )}
    />
  );
};
